import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import api, {normalizeApiError} from "../../system/api";
import Avatar from "../../system/components/avatar";
import {useTranslate} from "../../system/useTranslate";
import {
    activeCategories,
    applyTargetToAll,
    destinationMembers,
    firstInvalidCategory,
    initialDecisions,
    newIdempotencyKey,
    offboardingPayload,
    offboardingSteps,
} from "./workspace-offboarding";

// «حذف عضو از فضای کاری»: see what the member is responsible for, decide each item, review, remove.
// Everything is applied by the server in one transaction (POST /workspace-offboarding/:membership).

const STALE_ERRORS = new Set(["workspace-offboarding.error.stale-impact", "workspace-offboarding.error.already-removed"]);
const PREVIEW_ITEMS = 3;

const errorMessage = (error, fallback) => {
    const normalized = error?.normalizedError || normalizeApiError(error);
    const message = normalized?.message;
    return typeof message === "string" && message ? message : fallback;
};

function WorkspaceOffboardingDialog({visible, row, rows, workspaceDocumentId, onHide, onRemoved}) {
    const translate = useTranslate();
    const [impact, setImpact] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState("impact");
    const [decisions, setDecisions] = useState({});
    const [allTarget, setAllTarget] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const idempotencyKey = useRef(newIdempotencyKey());

    const membershipId = row?.membershipId;
    const memberName = row?.member?.fullname || row?.member?.username || translate("workspace-members.unknown-member");
    const headers = useMemo(() => ({workspace: workspaceDocumentId}), [workspaceDocumentId]);

    const destinations = useMemo(() => destinationMembers(rows, membershipId).map((item) => ({
        value: item.membershipId,
        label: item.member?.fullname || item.member?.username || translate("workspace-members.unknown-member"),
        member: item.member,
    })), [membershipId, rows, translate]);
    const destinationName = (value) => destinations.find((item) => String(item.value) === String(value))?.label || "";

    const loadImpact = useCallback(async () => {
        if (!membershipId) return;
        setLoading(true);
        setLoadError("");
        setSubmitError("");
        try {
            const response = await api.get(`/workspace-offboarding/${membershipId}/impact`, {headers, suppressGlobalErrorHandler: true});
            const data = response?.data?.data;
            setImpact(data);
            setDecisions(initialDecisions(data));
            setAllTarget(null);
            setStep("impact");
        } catch (error) {
            setImpact(null);
            setLoadError(translate(errorMessage(error, "workspace-offboarding.load-failed")));
        } finally {
            setLoading(false);
        }
    }, [headers, membershipId, translate]);

    useEffect(() => {
        if (!visible) return;
        idempotencyKey.current = newIdempotencyKey();
        loadImpact();
    }, [visible, loadImpact]);

    const steps = offboardingSteps(impact);
    const stepIndex = Math.max(0, steps.indexOf(step));
    const categories = activeCategories(impact);
    const invalidCategory = firstInvalidCategory(impact, decisions);

    const setDecision = (key, patch) => setDecisions((current) => ({...current, [key]: {...current[key], ...patch}}));

    const goNext = () => {
        if (step === "transfer" && invalidCategory) {
            const category = categories.find((item) => item.key === invalidCategory);
            setSubmitError(translate(category?.transferRequired || decisions[invalidCategory]?.action === "transfer"
                ? "workspace-offboarding.error.transfer-required"
                : "workspace-offboarding.error.invalid-decision"));
            return;
        }
        setSubmitError("");
        setStep(steps[Math.min(steps.length - 1, stepIndex + 1)]);
    };
    const goBack = () => {
        setSubmitError("");
        setStep(steps[Math.max(0, stepIndex - 1)]);
    };

    const submit = async () => {
        if (submitting || invalidCategory) return;
        setSubmitting(true);
        setSubmitError("");
        try {
            await api.post(
                `/workspace-offboarding/${membershipId}`,
                {data: offboardingPayload(impact, decisions, idempotencyKey.current)},
                {headers, suppressGlobalErrorHandler: true},
            );
            await onRemoved?.(translate("workspace-offboarding.success"));
        } catch (error) {
            const message = errorMessage(error, "workspace-offboarding.error.submit");
            setSubmitError(translate(message));
            if (STALE_ERRORS.has(message)) setImpact((current) => current && {...current, stale: true});
        } finally {
            setSubmitting(false);
        }
    };

    const categoryLine = (category) => translate(`workspace-offboarding.category.${category.key}`).replace("{{count}}", category.count);

    const impactStep = (
        <div className="flex flex-column gap-3">
            <p className="text-xs text-700 line-height-3 m-0">{translate("workspace-offboarding.impact-description")}</p>
            {categories.length === 0 ? (
                <Message severity="success" className="text-xs justify-content-start" text={translate("workspace-offboarding.no-current-responsibilities")}/>
            ) : (
                <ul className="list-none p-0 m-0 flex flex-column gap-2">
                    {categories.map((category) => (
                        <li key={category.key} className="surface-50 border-round-lg px-3 py-2 flex flex-column gap-1">
                            <strong className="text-xs text-900">{categoryLine(category)}</strong>
                            <small className="text-2xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {category.items.slice(0, PREVIEW_ITEMS).map((item) => item.title || "—").join("، ")}
                                {category.count > PREVIEW_ITEMS ? " …" : ""}
                            </small>
                        </li>
                    ))}
                </ul>
            )}
            <small className="text-2xs text-500 line-height-3">{translate("workspace-offboarding.history-preserved")}</small>
        </div>
    );

    const destinationItem = (option) => option && (
        <span className="flex align-items-center gap-2 text-xs">
            <Avatar memberData={option.member} className="text-2xs" size="small"/>
            {option.label}
        </span>
    );

    const transferStep = (
        <div className="flex flex-column gap-3">
            <p className="text-xs text-700 line-height-3 m-0">{translate("workspace-offboarding.transfer-description")}</p>
            <label className="flex flex-column gap-2">
                <span className="text-xs text-900 font-medium">{translate("workspace-offboarding.apply-member-to-all")}</span>
                <Dropdown
                    value={allTarget}
                    options={destinations}
                    onChange={(event) => {
                        setAllTarget(event.value);
                        setDecisions((current) => applyTargetToAll(impact, current, event.value));
                    }}
                    placeholder={translate("workspace-offboarding.select-destination")}
                    itemTemplate={destinationItem}
                    filter
                    className="w-full text-xs"
                />
            </label>
            <ul className="list-none p-0 m-0 flex flex-column gap-2">
                {categories.map((category) => {
                    const decision = decisions[category.key] || {};
                    const invalid = invalidCategory === category.key && Boolean(submitError);
                    return (
                        <li key={category.key} className={`border-1 border-round-lg px-3 py-2 flex flex-column md:flex-row md:align-items-center gap-2 ${invalid ? "border-red-300" : "border-100"}`}>
                            <span className="flex-1 flex flex-column gap-1 min-w-0">
                                <strong className="text-xs text-900">{translate(`workspace-offboarding.category-label.${category.key}`)}</strong>
                                <small className="text-2xs text-500">{categoryLine(category)}</small>
                            </span>
                            {category.actions.length > 1 && (
                                <Dropdown
                                    value={decision.action}
                                    options={category.actions.map((action) => ({value: action, label: translate(`workspace-offboarding.action.${action}`)}))}
                                    onChange={(event) => setDecision(category.key, {action: event.value})}
                                    placeholder={translate("workspace-offboarding.select-action")}
                                    className="text-xs md:w-12rem"
                                />
                            )}
                            {decision.action === "transfer" && (
                                <Dropdown
                                    value={decision.target}
                                    options={destinations}
                                    onChange={(event) => setDecision(category.key, {target: event.value})}
                                    placeholder={translate("workspace-offboarding.select-destination")}
                                    itemTemplate={destinationItem}
                                    filter
                                    className="text-xs md:w-13rem"
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
            {categories.some((category) => decisions[category.key]?.action === "detach") && (
                <small className="text-2xs text-500 line-height-3">{translate("workspace-offboarding.detach-note")}</small>
            )}
        </div>
    );

    const reviewLine = (category) => {
        const decision = decisions[category.key] || {};
        if (decision.action === "transfer") {
            return translate("workspace-offboarding.review.transfer").replace("{{name}}", destinationName(decision.target));
        }
        return translate(`workspace-offboarding.review.${decision.action}`);
    };

    const reviewStep = (
        <div className="flex flex-column gap-3">
            {categories.length > 0 && (
                <>
                    <p className="text-xs text-700 line-height-3 m-0">{translate("workspace-offboarding.review-description")}</p>
                    <ul className="list-none p-0 m-0 flex flex-column gap-2">
                        {categories.map((category) => (
                            <li key={category.key} className="surface-50 border-round-lg px-3 py-2 flex align-items-center justify-content-between gap-2">
                                <span className="text-xs text-900">{categoryLine(category)}</span>
                                <span className="text-xs text-primary-700">{reviewLine(category)}</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
            <Message severity="warn" className="text-xs justify-content-start" text={translate("workspace-offboarding.final-warning")}/>
            <small className="text-2xs text-500 line-height-3">{translate("workspace-offboarding.other-workspaces-safe")}</small>
        </div>
    );

    const footer = impact && !loading ? (
        <div className="flex align-items-center justify-content-between gap-2">
            <Button
                text
                className="text-xs"
                label={translate(stepIndex === 0 ? "cancel" : "workspace-offboarding.back")}
                onClick={stepIndex === 0 ? onHide : goBack}
                disabled={submitting}
            />
            {impact.stale ? (
                <Button className="text-xs gap-2" icon="pi pi-refresh" label={translate("workspace-offboarding.retry")} onClick={loadImpact}/>
            ) : step === "review" ? (
                <Button
                    severity="danger"
                    className="text-xs"
                    label={translate("remove-from-workspace")}
                    loading={submitting}
                    onClick={submit}
                />
            ) : (
                <Button className="text-xs" label={translate("workspace-offboarding.continue")} onClick={goNext}/>
            )}
        </div>
    ) : null;

    return (
        <Dialog
            visible={visible}
            onHide={() => !submitting && onHide()}
            header={translate("workspace-offboarding.title").replace("{{name}}", memberName)}
            footer={footer}
            className="rtl shabnam-font w-11 md:w-8 lg:w-6 xl:w-5"
            contentClassName="text-right"
            dismissableMask={!submitting}
            draggable={false}
        >
            <div className="flex flex-column gap-3" dir="rtl">
                {impact && (
                    <div className="flex align-items-center gap-2" aria-label={translate("workspace-offboarding.progress")}>
                        {steps.map((item, index) => (
                            <span key={item} className={`flex-1 border-round ${index <= stepIndex ? "bg-primary" : "surface-200"}`} style={{height: "4px"}}/>
                        ))}
                    </div>
                )}
                {loading && (
                    <div className="flex justify-content-center py-5">
                        <ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/>
                    </div>
                )}
                {!loading && loadError && (
                    <div className="flex flex-column align-items-center gap-3 py-3">
                        <Message severity="error" className="text-xs" text={loadError}/>
                        <Button className="text-xs gap-2" icon="pi pi-refresh" label={translate("workspace-offboarding.retry")} onClick={loadImpact}/>
                    </div>
                )}
                {!loading && impact && (
                    <>
                        {step === "impact" && impactStep}
                        {step === "transfer" && transferStep}
                        {step === "review" && reviewStep}
                    </>
                )}
                {submitError && <Message severity="error" className="text-xs justify-content-start" text={submitError}/>}
            </div>
        </Dialog>
    );
}

export default WorkspaceOffboardingDialog;
